import React, { useEffect, useRef, useState } from "react";
import { EMPLOYEE_STATUS_OPTIONS, UI } from "./employeeStyles";
import { fetchTutorById, saveTutor } from "./employeeFirestore";
import { statusLabelFromRaw } from "../../../../utils/userStatus";

function normalizeStatus(raw) {
  return statusLabelFromRaw(raw);
}

export default function EmployeeDetail({ uid, onBack }) {
  const [loading, setLoading] = useState(true);
  const [emp, setEmp]         = useState(null);
  const [saving, setSaving]   = useState(false);
  const noteRef               = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await fetchTutorById(uid);
      setEmp(data);
      setLoading(false);
    })();
  }, [uid]);

  useEffect(() => {
    if (!noteRef.current) return;
    noteRef.current.style.height = "auto";
    noteRef.current.style.height = `${noteRef.current.scrollHeight}px`;
  }, [emp?.note]);

  const setField = (k, v) => setEmp((p) => ({ ...(p || {}), [k]: v }));

  async function handleSave() {
    if (!emp) return;
    setSaving(true);
    try {
      const tRate = emp.teachingRate !== "" && emp.teachingRate != null ? parseFloat(emp.teachingRate) : null;
      const aRate = emp.adminRate    !== "" && emp.adminRate    != null ? parseFloat(emp.adminRate)    : null;

      if ((emp.teachingRate && isNaN(tRate)) || (emp.adminRate && isNaN(aRate))) {
        alert("Please enter valid numbers for the pay rates.");
        setSaving(false);
        return;
      }

      await saveTutor(uid, {
        name:             emp.name             || "",
        email:            emp.email            || "",
        phone:            emp.phone            || "",
        homeAddress:      emp.homeAddress      || "",
        dob:              emp.dob              || "",
        startWorkingDate: emp.startWorkingDate || "",
        endWorkingDate:   emp.endWorkingDate   || "",
        status:           normalizeStatus(emp.status),
        diagnosticSAT:    emp.diagnosticSAT    ?? "",
        note:             emp.note             || "",
        teachingRate:     tRate,
        adminRate:        aRate,
        kioskPin:         emp.kioskPin         || "",
      });
      alert("Saved successfully.");
    } catch (e) {
      console.error(e);
      alert("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ padding: 28, color: "#94a3b8", fontSize: 14 }}>Loading…</div>;
  }

  if (!emp) {
    return (
      <div style={{ padding: 28 }}>
        <button style={UI.backBtn} onClick={onBack}>← Back</button>
        <div style={{ marginTop: 16, color: "#b91c1c", fontSize: 14 }}>Employee not found.</div>
      </div>
    );
  }

  const status    = normalizeStatus(emp.status);
  const statusKey = status.toLowerCase();
  const role      = (emp.role || "tutor").toLowerCase();

  return (
    <div style={UI.detailPage}>
      {/* ── Top bar ── */}
      <div style={UI.detailTopBar}>
        <button style={UI.backBtn} onClick={onBack}>← Back</button>

        <div style={UI.detailHeaderInfo}>
          <div style={UI.detailName}>{emp.name || "Employee"}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5 }}>
            <span style={{ ...UI.statusBadge(statusKey), display: "inline-flex" }}>
              <span style={UI.statusDot(statusKey)} />
              {status}
            </span>
            <span style={{ fontSize: 12, color: "#94a3b8", textTransform: "capitalize" }}>
              {role}
            </span>
          </div>
        </div>

        <button
          style={{ ...UI.saveBtn, ...(saving ? UI.saveBtnDisabled : {}) }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {/* ── Scrollable body ── */}
      <div style={UI.detailScroll}>
        {/* Two-column grid — wraps to single column on narrow screens */}
        <div style={UI.detailColumns}>

          {/* LEFT column */}
          <div style={UI.detailColumn}>

            <Section title="Account Information">
              <FieldRow label="Full Name">
                <input
                  style={UI.fieldInput}
                  value={emp.name || ""}
                  onChange={(e) => setField("name", e.target.value)}
                />
              </FieldRow>
              <FieldRow label="Email">
                <input
                  style={UI.fieldInput}
                  value={emp.email || ""}
                  onChange={(e) => setField("email", e.target.value)}
                />
              </FieldRow>
              <FieldRow label="Status" last>
                <select
                  style={UI.fieldSelect}
                  value={status}
                  onChange={(e) => setField("status", e.target.value)}
                >
                  {EMPLOYEE_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </FieldRow>
            </Section>

            <Section title="Personal Information">
              <FieldRow label="Phone">
                <input
                  style={UI.fieldInput}
                  value={emp.phone || ""}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder="(555) 000-0000"
                />
              </FieldRow>
              <FieldRow label="Date of Birth">
                <input
                  type="date"
                  style={UI.fieldInputDate}
                  value={emp.dob || ""}
                  onChange={(e) => setField("dob", e.target.value)}
                />
              </FieldRow>
              <FieldRow label="Home Address" last>
                <input
                  style={UI.fieldInput}
                  value={emp.homeAddress || ""}
                  onChange={(e) => setField("homeAddress", e.target.value)}
                  placeholder="Street, City, State"
                />
              </FieldRow>
            </Section>

          </div>

          {/* RIGHT column */}
          <div style={UI.detailColumn}>

            <Section title="Employment">
              <FieldRow label="Start Date">
                <input
                  type="date"
                  style={UI.fieldInputDate}
                  value={emp.startWorkingDate || ""}
                  onChange={(e) => setField("startWorkingDate", e.target.value)}
                />
              </FieldRow>
              <FieldRow label="End Date">
                <input
                  type="date"
                  style={UI.fieldInputDate}
                  value={emp.endWorkingDate || ""}
                  onChange={(e) => setField("endWorkingDate", e.target.value)}
                />
              </FieldRow>
              <FieldRow label="Diagnostic SAT" last>
                <input
                  type="number"
                  inputMode="numeric"
                  style={{ ...UI.fieldInput, maxWidth: 140 }}
                  value={emp.diagnosticSAT ?? ""}
                  onChange={(e) => setField("diagnosticSAT", e.target.value)}
                  placeholder="—"
                  min="400"
                  max="1600"
                />
              </FieldRow>
            </Section>

            <Section title="Compensation">
              <FieldRow label="Teaching Rate">
                <RateInput
                  value={emp.teachingRate ?? ""}
                  onChange={(v) => setField("teachingRate", v)}
                />
              </FieldRow>
              <FieldRow label="Admin Rate"> {/* <-- Removed 'last' attribute here */}
                <RateInput
                  value={emp.adminRate ?? ""}
                  onChange={(v) => setField("adminRate", v)}
                />
              </FieldRow>
              <FieldRow label="Kiosk PIN" last> {/* <-- Added this new row as 'last' */}
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="0000"
                  style={{ ...UI.fieldInput, maxWidth: 120 }}
                  value={emp.kioskPin || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Allows empty string or only digits up to 4 characters
                    if (val === "" || (/^\d+$/.test(val) && val.length <= 4)) {
                      setField("kioskPin", val);
                    }
                  }}
                />
              </FieldRow>
            </Section>

          </div>
        </div>

        {/* Notes — full width */}
        <div style={{ maxWidth: 1040, marginTop: 18 }}>
          <div style={UI.notesCard}>
            <div style={UI.sectionHeader}>
              <div style={UI.sectionTitle}>Internal Notes</div>
            </div>
            <textarea
              ref={noteRef}
              value={emp.note || ""}
              placeholder="Add internal notes about this employee…"
              onChange={(e) => setField("note", e.target.value)}
              onInput={() => {
                if (!noteRef.current) return;
                noteRef.current.style.height = "auto";
                noteRef.current.style.height = `${noteRef.current.scrollHeight}px`;
              }}
              style={UI.notesTextarea}
              rows={5}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Small layout helpers ── */

function Section({ title, children }) {
  return (
    <div style={UI.sectionCard}>
      <div style={UI.sectionHeader}>
        <div style={UI.sectionTitle}>{title}</div>
      </div>
      <div style={{ padding: "2px 0" }}>{children}</div>
    </div>
  );
}

function FieldRow({ label, children, last }) {
  return (
    <div
      style={{
        ...UI.fieldRow,
        borderBottom: last ? "none" : "1px solid #f8fafc",
      }}
    >
      <div style={UI.fieldLabel}>{label}</div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

function RateInput({ value, onChange }) {
  return (
    <div style={UI.rateWrap}>
      <span style={UI.rateCurrency}>$</span>
      <input
        type="text"
        inputMode="decimal"
        placeholder="0.00"
        style={UI.rateInput}
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "" || /^[0-9]*\.?[0-9]*$/.test(v)) onChange(v);
        }}
      />
      <span style={UI.rateUnit}>/hr</span>
    </div>
  );
}
